<template lang="pug">
NuxtLink.card-tournament(:to="tournamentLink")
  .card-tournament__head(:style="{backgroundImage: `url('${tournamentCover}')`}")
    .card-tournament__head-title {{ tournament.title }}
    .card-tournament__head-prize
      span.card-tournament__head-prize-label(v-t="'prizePool'")
      .card-tournament__head-prize-value
        template(v-if="isMoneyPrize")
          Currency(type="money" size="16")
            span {{ tournamentMoneyPrize }}
        template(v-else)
          Currency(type="coins" size="16")
            span {{ tournamentCoinPrize }}
          | +&nbsp;
          Currency(type="tickets" size="16")
            span {{ tournamentTrophyPrize }}

  .card-tournament__body
    .card-tournament__start-info
      InlineSvg.card-tournament__start-info-icon(:src="require('~/assets/img/icons/time.svg')")
      Countdown(:end-time="tournamentStart")
        span(slot="process" slot-scope="{ timeObj }")
          | {{ timeObj.h }}:{{ timeObj.m }}:{{ timeObj.s }}

    .card-tournament__body-bottom
      .card-tournament__body-bottom-left
        .card-tournament__participants
          InlineSvg.card-tournament__participants-icon(:src="require('~/assets/img/icons/min-players.svg')")
          | {{ playersAmount }}
        .card-tournament__entry-price
          Currency(v-if="tournamentWithEntranceTicket" type="money" is-reversed)
            span {{ tournamentEntryFee }}
          NuxtLink.card-tournament__link(v-else-if="prizeInCoins" :to="localeLocation('/store/subscriptions')") {{ $t('subscription') }}
      Button.card-tournament__button(is-primary) {{ $t("participate") }}
</template>

<script>
import { getDateTextForTourCard } from '@/utils/utils'
import { mapState } from 'vuex'

export default {
  name: 'TournamentCardNewDesign',

  props: {
    tournament: {
      type: Object,
      required: true
    }
  },

  computed: {
    ...mapState('games', ['gamesById']),

    game () {
      return this.gamesById[this.tournament?.game_id]
    },

    gameName () {
      return this.game?.name
    },

    isGamePix () {
      return this.game.name === 'game-pix'
    },

    subGame () {
      return this.tournament?.sub_game
    },

    tournamentCover () {
      return this.tournament?.image_url
    },

    tournamentPageUrl () {
      if (this.isGamePix) {
        return this.localeLocation(`/games/gamepix/${this.subGame}/${this.tournamentId}`)
      }
      return this.localeLocation(`/${this.gameName}/tournaments/${this.tournamentId}`)
    },

    tournamentLink () {
      return this.tournamentFormat === 'Multi stage'
        ? this.localeLocation(`/${this.gameName}/tournaments/new/m${this.tournament?.mega_tournament_id}`)
        : this.tournamentPageUrl
    },

    tournamentId () {
      return this.tournament?.id
    },

    tournamentWithEntranceTicket () {
      return this.tournament?.prize_settings?.entry_fee_currency === 'money'
    },

    prizeInCoins () {
      return this.prizeCurrency === 'coins'
    },

    tournamentEntryFee () {
      return (this.tournament?.entry_fee / 100).toFixed(2).replace('.00', '')
    },

    playersAmount () {
      return `${this.tournament?.players_count} ${this.$t('of')} ${this.tournament?.tournament_player_settings?.max_players_count}`
    },

    tournamentFormat () {
      return this.tournament?.mega_tournament_id ? 'Multi stage' : 'Default'
    },

    prizeCurrency () {
      return this.tournament?.prize_settings?.prize_currency
    },

    isMoneyPrize () {
      return this.prizeCurrency === 'money'
    },

    tournamentCoinPrize () {
      let prizePool = this.tournament?.prize_pool

      if (this.risingPrizePool) {
        prizePool = this.maxPrizePool
      }

      return this.isMoneyPrize ? prizePool * 12 : prizePool
    },

    tournamentTrophyPrize () {
      return Math.ceil(this.tournamentCoinPrize / 1000).toFixed(0)
    },

    maxPrizePool () {
      let membersCount =
        this.tournament?.tournament_player_settings?.max_players_count

      if (this.tournament?.teams) {
        membersCount =
          this.tournament?.tournament_player_settings?.max_players_count *
          this.tournament?.tournament_player_settings?.team_size
      }

      const entryFee = this.tournament?.prize_settings?.entry_fee_currency === 'money' ? this.tournament.entry_fee / 100 : this.tournament.entry_fee

      return (
        Number(entryFee -
          (entryFee / 100) * this.tournament?.prize_settings?.rake) *
        membersCount
      ).toFixed(2).replace('.00', '')
    },

    risingPrizePool () {
      return ['not_fixed_with_guarantee', 'not_fixed'].includes(this.tournament?.prize_settings?.prize_pool_type)
    },

    tournamentMoneyPrize () {
      let prizePool = (this.tournament?.prize_pool / 100).toFixed(2).replace('.00', '')

      if (this.risingPrizePool) {
        prizePool = this.maxPrizePool
      }
      return this.isMoneyPrize ? prizePool : '0'
    },

    tournamentStart () {
      return this.tournament?.date?.replace(' ', 'T') + 'Z'
    },

    tournamentRegistrationStart () {
      return this.tournament?.registration_starts_at?.replace(' ', 'T') + 'Z'
    },

    tournamentStartText () {
      if (this.tournamentStart) {
        return getDateTextForTourCard.call(this, this.tournamentStart, this.tournamentRegistrationStart)
      }
      return '...'
    },

    isSponsored () {
      return this.tournament?.is_sponsored
    },

    tournamentRating () {
      if (!this.tournament?.min_rating_group && !this.tournament?.max_rating_group) {
        return 'All'
      }
      if (!this.tournament?.min_rating_group) {
        return `D - ${this.tournament?.max_rating_group}`
      }
      if (!this.tournament?.max_rating_group) {
        return `${this.tournament?.min_rating_group} - S`
      }
      if (this.tournament?.min_rating_group === this.tournament?.max_rating_group) {
        return `${this.tournament?.max_rating_group}`
      }
      return `${this.tournament?.min_rating_group} - ${this.tournament?.max_rating_group}`
    },

    tournamentRegion () {
      return this.tournament?.region || 'All'
    },

    starsCount () {
      return Math.ceil(this.tournament?.avg_star_count || 0)
    }
  }
}
</script>

<style lang="scss" scoped>
.card-tournament {
  display: block;
  border-radius: 16px;
  background: $blue-grey70;
  overflow: hidden;

  &__head {
    padding: 12px 16px;
    border-radius: 16px;

    &-title {
      margin-bottom: 16px;
      font-size: 16px;
      font-weight: 700;
      line-height: 22px;
      color: $white;
    }

    &-prize {
      &-label {
        color: $green50;
        font-size: 10px;
        font-style: italic;
        font-weight: 500;
        line-height: 100%;
        letter-spacing: 0.2px;
        text-transform: uppercase;
      }

      &-value {
        color: $white;
        font-size: 14px;
        font-weight: 700;
        line-height: 16px;
      }
    }

    &-aditional {
      display: none;
    }
  }

  &__start-info {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 8px;
    margin: 0;
    border-radius: 8px;
    background: $blue-grey50;

    &-icon {
      width: 16px;
      height: 16px;
      margin-right: 8px;

      ::v-deep path {
        fill: $white;
      }
    }
  }

  &__body {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 12px;
    padding: 12px;
    background: $blue-grey70;

    &-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &-left {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
      }
    }
  }

  &__link {
    color: $primary60;
    text-decoration: underline;
  }

  &__participants {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    color: $white;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;

    &-icon {
      width: 20px;
      height: 20px;
      margin-right: 8px;

      ::v-deep path {
        fill: $white;
      }
    }
  }
}
</style>
